/**
 * Snap-to-grid: find the nearest empty hex cell for a projectile landing position.
 */

import type { HexCoord, Vec2 } from '../grid/hex';
import { pixelToHex, hexToPixel, hexNeighbors, isValidCoord, coordToKey } from '../grid/hex';
import type { BoardState } from '../types';
import { getBubble } from '../types';
import { CONFIG } from '../config';

/**
 * Check if a cell has at least one occupied hex neighbor on the board.
 */
function hasOccupiedNeighbor(board: BoardState, coord: HexCoord): boolean {
  for (const n of hexNeighbors(coord)) {
    if (getBubble(board, n) !== undefined) return true;
  }
  return false;
}

/**
 * Safety cap on BFS expansion. 60 rings covers the full board many times over;
 * in practice the search terminates within 1–3 rings. The cap only protects
 * against a bug placing the loop in an infinite state.
 */
const MAX_RINGS = 60;

/**
 * Given a pixel position, find the nearest empty hex cell on the board
 * that is adjacent to at least one existing bubble (the "no floating
 * placements" invariant, except when the board is empty).
 *
 * Uses a BFS ring expansion starting from the approximate cell. Returns the
 * closest-to-`position` candidate found within the first ring that contains
 * any valid candidate. Bails out if the expansion exhausts all reachable
 * cells or hits the safety cap without finding one.
 *
 * Correct behavior on a packed board: the approximate cell (the bubble we
 * collided with) has no empty neighbors, but its neighbors' neighbors do —
 * the BFS pushes outward until it finds them, unlike the fixed 2-ring
 * search that used to leave snapToGrid returning null when the cluster
 * filled the local neighborhood.
 *
 * @param position  Pixel position of the projectile at collision/ceiling hit
 * @param board     Current board state to check occupancy
 * @returns The nearest valid HexCoord, or null if no valid cell exists
 */
export function snapToGrid(position: Vec2, board: BoardState): HexCoord | null {
  const approx = pixelToHex(position, CONFIG.CELL_SIZE);
  const boardEmpty = board.bubbles.size === 0;

  // Special case: empty board — place at approx if in bounds.
  if (boardEmpty) {
    if (isValidCoord(approx, board.width, board.ceilingRow)) return approx;
    return null;
  }

  const seen = new Set<string>();
  let frontier: HexCoord[] = [approx];
  seen.add(coordToKey(approx));

  let bestCoord: HexCoord | null = null;
  let bestDistSq = Infinity;

  for (let ring = 0; ring < MAX_RINGS; ring++) {
    // Evaluate every cell in the current frontier as a candidate. Once we
    // find any valid candidate at this ring, we still check the remaining
    // frontier at the same ring (same BFS distance) to pick the closest
    // by pixel distance, but we do NOT expand further rings — the answer
    // is guaranteed to be at or closer than the next ring.
    for (const coord of frontier) {
      if (!isValidCoord(coord, board.width, board.ceilingRow)) continue;
      if (getBubble(board, coord) !== undefined) continue;
      if (!hasOccupiedNeighbor(board, coord)) continue;

      const cellPos = hexToPixel(coord, CONFIG.CELL_SIZE);
      const dx = position.x - cellPos.x;
      const dy = position.y - cellPos.y;
      const distSq = dx * dx + dy * dy;
      if (distSq < bestDistSq) {
        bestDistSq = distSq;
        bestCoord = coord;
      }
    }

    if (bestCoord) return bestCoord;

    // Expand to the next ring: neighbors of the current frontier not yet seen.
    const nextFrontier: HexCoord[] = [];
    for (const coord of frontier) {
      for (const n of hexNeighbors(coord)) {
        const key = coordToKey(n);
        if (seen.has(key)) continue;
        seen.add(key);
        nextFrontier.push(n);
      }
    }
    if (nextFrontier.length === 0) break;
    frontier = nextFrontier;
  }

  return null;
}
